import { type ElementType } from 'react';
import {
  FUEL_TYPE_CARDS,
  VEHICLE_TYPE_CARDS,
  REFRIGERANT_TYPE_CARDS,
  PROCESS_TYPE_CARDS,
} from '../components/form/types';
import type { SourceSubTypeField } from '../types/emissions';

export interface ResolvedSourceIcon {
  Icon: ElementType;
  iconBg: string;
  iconColor: string;
}

// Build lookup maps once for O(1) access. The card arrays are the single
// source of truth for sub-type icons — reuse them so the breakdown table
// matches the Add Activities flow visually.
const FUEL_ICON_MAP: Record<string, ResolvedSourceIcon> = Object.fromEntries(
  FUEL_TYPE_CARDS.map((c) => [c.value, { Icon: c.Icon, iconBg: c.iconBg, iconColor: c.iconColor }]),
);
const VEHICLE_ICON_MAP: Record<string, ResolvedSourceIcon> = Object.fromEntries(
  VEHICLE_TYPE_CARDS.map((c) => [c.value, { Icon: c.Icon, iconBg: c.iconBg, iconColor: c.iconColor }]),
);
const GAS_ICON_MAP: Record<string, ResolvedSourceIcon> = Object.fromEntries(
  REFRIGERANT_TYPE_CARDS.map((c) => [c.value, { Icon: c.Icon, iconBg: c.iconBg, iconColor: c.iconColor }]),
);
const PROCESS_ICON_MAP: Record<string, ResolvedSourceIcon> = Object.fromEntries(
  PROCESS_TYPE_CARDS.map((c) => [c.value, { Icon: c.Icon, iconBg: c.iconBg, iconColor: c.iconColor }]),
);

/**
 * Returns the icon + background/foreground classes for a sub-type row.
 * Returns `null` when no icon is defined for the given combination (the
 * table should render no badge at all rather than a placeholder).
 */
export const resolveSourceIcon = (
  subTypeField: SourceSubTypeField,
  subTypeValue: string | null,
): ResolvedSourceIcon | null => {
  if (!subTypeField || !subTypeValue) return null;
  switch (subTypeField) {
    case 'fuelType':
      return FUEL_ICON_MAP[subTypeValue] ?? null;
    case 'vehicleType':
      return VEHICLE_ICON_MAP[subTypeValue] ?? null;
    case 'refrigerantType':
      return GAS_ICON_MAP[subTypeValue] ?? null;
    case 'processType':
      return PROCESS_ICON_MAP[subTypeValue] ?? null;
  }
};
